import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PostflightOrchestrationPanel } from "@/components/PostflightOrchestrationPanel";
import { px4SourceLabel } from "@/lib/px4Source";
import {
  X, Stethoscope, Loader2, CheckCircle2, AlertTriangle, XOctagon, HelpCircle,
  Sparkles, Copy, Eye, ChevronDown, ChevronRight, RefreshCw, GitCompareArrows,
} from "lucide-react";

const SEV = {
  ok: { color: "#22C55E", label: "OK", Icon: CheckCircle2 },
  warning: { color: "#F59E0B", label: "ATTENTION", Icon: AlertTriangle },
  critical: { color: "#EF4444", label: "CRITIQUE", Icon: XOctagon },
  unknown: { color: "#737373", label: "INCONNU", Icon: HelpCircle },
};
const sev = (s) => SEV[s] || SEV.unknown;

// A.5.4H.4C — couleur par sévérité MAV_SEVERITY réellement reçue (jamais fabriquée).
const PX4_SEV_COLOR = {
  EMERGENCY: "#EF4444", ALERT: "#EF4444", CRITICAL: "#EF4444", ERROR: "#EF4444",
  WARNING: "#F59E0B", NOTICE: "#3B82F6", INFO: "#A3A3A3", DEBUG: "#525252",
};

function px4Label(ev) {
  if (ev.kind === "statustext") return (ev.text || "") + (ev.text_incomplete ? "  [texte incomplet]" : "");
  if (ev.kind === "event") return `EVENT #${ev.event_id ?? "?"} (seq ${ev.sequence ?? "?"})`;  // non décodé : ID brut
  if (ev.transition === "event_sequence") return `Séquence PX4 : ${ev.sequence_status}${ev.dropped ? ` (${ev.dropped} perdu(s))` : ""}`;
  return `${ev.transition} : ${String(ev.from)} → ${String(ev.to)}`;
}

// A.5.4H.4C.2 — source lisible (PX4 vs observation SkyCore) via helper pur @/lib/px4Source.

export default function DroneDiagnosticModal({ droneId, onClose }) {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [postflight, setPostflight] = useState(null);
  const [pfLoading, setPfLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/drones/${droneId}/diagnostic`);
      setReport(data);
    } catch (e) { setError(apiErr(e, "Diagnostic indisponible")); }
    finally { setLoading(false); }
  }, [droneId]);

  useEffect(() => { load(); }, [load]);

  const sendToAI = () => {
    if (!report) return;
    navigate("/assistant", {
      state: { prefill: report.ai_context.recommended_prompt, context: "drone", autoSend: true, droneId },
    });
  };

  const runPostflight = async () => {
    if (!report?.last_orchestration) return;
    setPfLoading(true);
    try {
      const { data } = await api.post(`/drones/${droneId}/postflight-summary`);
      setPostflight(data);
      if (data?.available === false) toast.info(data.message || "Aucune orchestration enregistrée.");
    } catch (e) { toast.error(apiErr(e, "Synthèse post-vol indisponible")); }
    finally { setPfLoading(false); }
  };

  const copyReport = async () => {
    const text = report?.ai_context?.report_text || "";
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Rapport copié");
      return;
    } catch { /* fallback below */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Rapport copié");
    } catch { toast.error("Copie impossible"); }
  };

  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));
  const groups = report ? [...new Set(report.checks.map((c) => c.group))] : [];
  const s = report ? sev(report.summary_status) : sev("unknown");

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="diagnostic-modal" onClick={onClose}>
      <div className="bg-[#0F0F0F] border border-white/15 rounded-sm w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 shrink-0">
          <div className="w-8 h-8 border border-white/15 rounded-sm flex items-center justify-center">
            <Stethoscope size={16} className="text-[#3B82F6]" />
          </div>
          <div>
            <div className="font-chivo text-lg font-black tracking-tighter uppercase leading-none">Diagnostic drone</div>
            <div className="font-mono text-[10px] text-[#A3A3A3] mt-0.5">{droneId} · lecture seule</div>
          </div>
          <button data-testid="diagnostic-refresh" onClick={load} disabled={loading}
            className="ml-auto w-7 h-7 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10 disabled:opacity-40">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          <button data-testid="diagnostic-close" onClick={onClose}
            className="w-7 h-7 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 font-mono text-xs text-[#525252]">
              <Loader2 size={16} className="animate-spin" /> Analyse du drone…
            </div>
          )}
          {error && !loading && (
            <div className="font-mono text-xs text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/10 rounded-sm p-3">{error}</div>
          )}

          {report && !loading && (
            <>
              {/* Synthèse globale */}
              <div data-testid="diagnostic-summary" className="rounded-sm border p-4 flex items-center gap-3"
                style={{ borderColor: `${s.color}55`, backgroundColor: `${s.color}12` }}>
                <s.Icon size={26} style={{ color: s.color }} className="shrink-0" />
                <div>
                  <div className="font-chivo text-xl font-black tracking-tighter uppercase" style={{ color: s.color }}>{s.label}</div>
                  <div className="font-mono text-[11px] text-[#A3A3A3]">{report.summary_line}</div>
                </div>
              </div>

              {/* Comparaison avec le diagnostic précédent (suivi dans le temps) */}
              {report.comparison?.has_previous && (
                <div data-testid="diagnostic-comparison"
                  className="rounded-sm border border-white/10 bg-[#141414] px-3 py-2 flex items-start gap-2">
                  <GitCompareArrows size={14} className="text-[#3B82F6] mt-0.5 shrink-0" />
                  <div className="font-mono text-[10px] text-[#A3A3A3]">
                    <span className="text-[#737373]">Depuis le dernier diagnostic ({timeAgo(report.comparison.previous_at)}) : </span>
                    {report.comparison.changes?.length ? (
                      <span>
                        {report.comparison.changes.map((c, i) => (
                          <span key={c.key}>
                            {i > 0 && ", "}
                            <span className="text-white">{c.label}</span>{" "}
                            <span style={{ color: sev(c.from).color }}>{sev(c.from).label}</span>
                            {" → "}
                            <span style={{ color: sev(c.to).color }}>{sev(c.to).label}</span>
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-[#22C55E]">aucun changement.</span>
                    )}
                  </div>
                </div>
              )}

              {/* Statuts synthétiques par domaine */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="diagnostic-statuses">
                {Object.entries(report.statuses).map(([k, v]) => {
                  const vs = sev(v.status);
                  return (
                    <div key={k} className="bg-[#141414] border border-white/10 rounded-sm px-2 py-2 text-center">
                      <div className="font-mono text-[8px] uppercase tracking-[0.1em] text-[#525252] leading-tight h-6 flex items-center justify-center">{v.label}</div>
                      <div className="font-mono text-[9px] font-bold uppercase mt-1" style={{ color: vs.color }}>{vs.label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Checks groupés */}
              <div className="space-y-3">
                {groups.map((g) => {
                  const gc = report.checks.filter((c) => c.group === g);
                  const gstat = report.statuses[g];
                  return (
                    <div key={g} data-testid={`diagnostic-group-${g}`}>
                      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3] mb-1.5">
                        {gstat?.label || g}
                      </div>
                      <div className="space-y-1">
                        {gc.map((c) => {
                          const cs = sev(c.status);
                          const hasDetails = c.details && Object.keys(c.details).length > 0;
                          const open = expanded[c.key];
                          return (
                            <div key={c.key} data-testid={`diagnostic-check-${c.key}`}
                              className="bg-[#141414] border border-white/10 rounded-sm">
                              <button onClick={() => hasDetails && toggle(c.key)}
                                className={`w-full flex items-start gap-2 px-3 py-2 text-left ${hasDetails ? "hover:bg-white/[0.03]" : ""}`}>
                                <cs.Icon size={14} style={{ color: cs.color }} className="shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="font-mono text-[11px] text-white">{c.label}</div>
                                  <div className="font-mono text-[10px] text-[#A3A3A3] break-words">{c.message}</div>
                                </div>
                                {hasDetails && (open ? <ChevronDown size={12} className="text-[#525252] mt-1" /> : <ChevronRight size={12} className="text-[#525252] mt-1" />)}
                              </button>
                              {hasDetails && open && (
                                <pre className="font-mono text-[9px] text-[#737373] bg-[#0A0A0A] border-t border-white/10 px-3 py-2 whitespace-pre-wrap break-words">
{JSON.stringify(c.details, null, 2)}
                                </pre>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* A.5.4H.4C — Événements PX4 récents (READ-ONLY). Graceful si Gateway indisponible. */}
              <div data-testid="diagnostic-px4-events">
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3] mb-1.5">
                  Événements PX4 récents
                </div>
                {!report.px4_events?.available ? (
                  <div data-testid="diagnostic-px4-unavailable"
                    className="font-mono text-[10px] text-[#525252] bg-[#141414] border border-white/10 rounded-sm px-3 py-2">
                    Événements PX4 indisponibles.
                  </div>
                ) : report.px4_events.recent?.length ? (
                  <div className="space-y-1">
                    {report.px4_events.recent.map((ev) => {
                      const col = ev.severity_label ? (PX4_SEV_COLOR[ev.severity_label] || "#A3A3A3") : "#06B6D4";
                      const c = ev.context || {};
                      return (
                        <div key={ev.event_uid} data-testid="diagnostic-px4-event"
                          className="bg-[#141414] border border-white/10 rounded-sm px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: col }} />
                            <span className="font-mono text-[9px] uppercase tracking-wider shrink-0" style={{ color: col }}>
                              {ev.severity_label || ev.kind}
                            </span>
                            <span data-testid="diagnostic-px4-source"
                              className="font-mono text-[9px] text-[#06B6D4] shrink-0">
                              {px4SourceLabel(ev.source)}
                            </span>
                            <span className="font-mono text-[9px] text-[#525252] shrink-0">{timeAgo(ev.ts_utc)}</span>
                            <span className="font-mono text-[10px] text-white truncate">{px4Label(ev)}</span>
                          </div>
                          <div className="font-mono text-[9px] text-[#737373] mt-0.5">
                            mode={c.mode ?? "—"} · armed={c.armed == null ? "—" : String(c.armed)}
                            {c.active_command_id ? ` · cmd=${c.active_command_name || "?"}(${c.active_command_id})` : ""}
                            {c.offboard_session_id ? ` · session=${c.offboard_session_id}` : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="font-mono text-[10px] text-[#525252] bg-[#141414] border border-white/10 rounded-sm px-3 py-2">
                    Aucun événement PX4 récent.
                  </div>
                )}
              </div>

              {/* A.5.4D — DERNIÈRE ORCHESTRATION (HISTORIQUE, distinct de l'état LIVE) */}
              <PostflightOrchestrationPanel
                orchestration={report.last_orchestration}
                summary={postflight?.available ? postflight.summary : null}
                containerTestid="diagnostic-last-orchestration"
                resultTestid="diagnostic-postflight-result"
                noOrchTestid="diagnostic-no-orchestration"
              />
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-white/10 shrink-0 flex-wrap">
          <button data-testid="diagnostic-send-ai" onClick={sendToAI} disabled={!report}
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40">
            <Sparkles size={14} /> Envoyer à l'IA
          </button>
          <button data-testid="diagnostic-postflight-summary-btn" onClick={runPostflight}
            disabled={!report?.last_orchestration || pfLoading}
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10 disabled:opacity-40">
            {pfLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Synthèse post-vol
          </button>
          <button data-testid="diagnostic-copy" onClick={copyReport} disabled={!report}
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10 disabled:opacity-40">
            <Copy size={13} /> Copier
          </button>
          <button data-testid="diagnostic-open-details" onClick={() => { onClose(); navigate(`/drones?id=${droneId}`); }}
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10">
            <Eye size={13} /> Détails drone
          </button>
          <span className="ml-auto font-mono text-[9px] text-[#525252]">Aucune action n'est exécutée par ce diagnostic.</span>
        </div>
      </div>
    </div>
  );
}
